import React from 'react';
import { Alert, Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useNavigate } from 'react-router-dom';
import DataTable from '../../../Shared/Components/DataTable';
import { createReservationColumns } from '../../columns';
import { useReservationTable } from '../../Hooks/useReservationTable';
import type { ReservationDataTableProps } from './ReservationDataTableTypes';
import { useDeleteReservationMutation } from '../../Service/reservationsApi';
import { extractApiErrorMessage } from '../../../../Utils/apiErrorUtils';
import { InvoicesRoutes } from '../../../../Router/path';
import { useSearchableReservationPartiesLookupQuery } from '../../Service/reservationPartiesLookupApi';
import type { Reservation } from '../../Types/reservationTypes';

const ReservationDataTable: React.FC<ReservationDataTableProps> = ({
  data,
  isLoading,
  isError,
  errorMessage,
  totalCount,
  applicantDisplayMode = 'split',
  showPaymentSummary = false,
  showCreateInvoiceAction = false,
  onRowDeleted,
  onSelectionChange,
  page: propPage,
  rowsPerPage: propRowsPerPage,
  onPageChange: propOnPageChange,
  onRowsPerPageChange: propOnRowsPerPageChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [deleteReservation, { isLoading: isDeletingReservation }] = useDeleteReservationMutation();
  const [deleteError, setDeleteError] = React.useState<string>('');

  const { selectedReservationIds, setSelectedReservationIds, handleEdit } = useReservationTable();
  const applicantPartyIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          data
            .map((reservation) => reservation.applicantPartyId)
            .filter((id): id is number => typeof id === 'number')
        )
      ),
    [data]
  );
  const { parties } = useSearchableReservationPartiesLookupQuery(
    applicantDisplayMode === 'summary' && applicantPartyIds.length > 0
      ? { ids: applicantPartyIds, take: applicantPartyIds.length }
      : undefined
  );
  const columns = React.useMemo(
    () => createReservationColumns({ applicantDisplayMode, parties }),
    [applicantDisplayMode, parties]
  );

  const handleSelectionChange = (ids: number[]) => {
    setSelectedReservationIds(ids);
    onSelectionChange?.(ids);
  };

  const isRowSelectable = (row: Reservation) => row.status !== 'cancelled';

  const getRowSx = (row: Reservation) =>
    row.status === 'cancelled' ? { opacity: 0.6, backgroundColor: 'action.hover' } : undefined;

  const handleDeleteRow = async (reservationId: number) => {
    if (!window.confirm('Czy na pewno chcesz anulować tę rezerwację?')) {
      return;
    }

    try {
      setDeleteError('');
      await deleteReservation(reservationId).unwrap();
      await onRowDeleted?.();
    } catch (error) {
      setDeleteError(extractApiErrorMessage(error, 'Nie udało się anulować rezerwacji.'));
    }
  };

  const handleCreateInvoice = (reservationId: number) => {
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    navigate(
      `${InvoicesRoutes.create}?reservationId=${reservationId}&returnTo=${encodeURIComponent(returnTo)}`
    );
  };

  const paymentSummary = React.useMemo(() => {
    return data.reduce(
      (summary, reservation) => {
        const cashPaid = reservation.paidAmount ?? 0;
        const nonCashPaid = reservation.nonCashPaymentAmount ?? 0;
        const totalPaid = reservation.totalPaidAmount ?? cashPaid + nonCashPaid;
        const totalPrice = reservation.totalPrice ?? 0;
        const remainingToPay = Math.max(totalPrice - totalPaid, 0);
        const overpayment = Math.max(totalPaid - totalPrice, 0);

        return {
          cashPaid: summary.cashPaid + cashPaid,
          nonCashPaid: summary.nonCashPaid + nonCashPaid,
          totalPaid: summary.totalPaid + totalPaid,
          totalPrice: summary.totalPrice + totalPrice,
          remainingToPay: summary.remainingToPay + remainingToPay,
          overpayment: summary.overpayment + overpayment,
        };
      },
      {
        cashPaid: 0,
        nonCashPaid: 0,
        totalPaid: 0,
        totalPrice: 0,
        remainingToPay: 0,
        overpayment: 0,
      }
    );
  }, [data]);

  return (
    <>
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deleteError}
        </Alert>
      )}
      <DataTable
        columns={columns}
        data={data}
        tableLayout={applicantDisplayMode === 'summary' ? 'fixed' : 'auto'}
        isLoading={isLoading || isDeletingReservation}
        isError={isError}
        errorMessage={errorMessage}
        getRowId={(row) => row.id}
        onEdit={(id) => handleEdit(id, data)}
        selected={selectedReservationIds}
        onSelectionChange={handleSelectionChange}
        page={propPage ?? 0}
        rowsPerPage={propRowsPerPage ?? 10}
        totalCount={totalCount}
        onPageChange={propOnPageChange}
        onRowsPerPageChange={propOnRowsPerPageChange}
        isRowSelectable={isRowSelectable}
        getRowSx={getRowSx}
        renderCustomActions={(row) => {
          if (row.status === 'cancelled') {
            // isRowSelectable also gates DataTable's own "Edytuj" action, so a cancelled row
            // needs its own view-only affordance here to stay reachable from the list.
            return (
              <Tooltip title="Podgląd">
                <span>
                  <IconButton
                    size="small"
                    color="default"
                    sx={{ p: 0.5 }}
                    onClick={() => handleEdit(row.id, data)}
                    aria-label="Podgląd"
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          }

          return (
            <>
              {showCreateInvoiceAction && (
                <Tooltip title="Wystaw FV">
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      sx={{ p: 0.5 }}
                      disabled={isDeletingReservation}
                      onClick={() => handleCreateInvoice(row.id)}
                      aria-label="Wystaw FV"
                    >
                      <ReceiptLongIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Anuluj">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    sx={{ p: 0.5 }}
                    disabled={isDeletingReservation}
                    onClick={() => handleDeleteRow(row.id)}
                    aria-label="Anuluj"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          );
        }}
      />

      {showPaymentSummary && !isLoading && !isError && data.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Podsumowanie wpłat:
          </Typography>
          <Chip label={`Gotówka: ${paymentSummary.cashPaid.toFixed(2)} PLN`} color="default" />
          <Chip
            label={`Bezgotówkowo: ${paymentSummary.nonCashPaid.toFixed(2)} PLN`}
            color="default"
          />
          <Chip label={`Łącznie: ${paymentSummary.totalPaid.toFixed(2)} PLN`} color="primary" />
          <Chip
            label={`Pozostało do zapłaty: ${paymentSummary.remainingToPay.toFixed(2)} PLN`}
            color="warning"
          />
          <Chip label={`Nadpłata: ${paymentSummary.overpayment.toFixed(2)} PLN`} color="info" />
        </Box>
      )}
    </>
  );
};

export default ReservationDataTable;
